// Tile icons for the demo account switcher, keyed by the three vocabularies the
// `GET /api/v1/demo_accounts` payload uses: customer-type key, org kind, and
// persona role. Solar has no church or landmark glyph, so faith reads as a
// bookmarked book and a US state reads as a flag.
import type { DemoOrg, DemoOrgKind } from '@/types/demo';
import {
  BackpackLinear,
  Book2Linear,
  BookBookmarkLinear,
  BookLinear,
  Buildings2Linear,
  BuildingsLinear,
  FlagLinear,
  GlobalLinear,
  MapPointLinear,
  PenLinear,
  PresentationGraphLinear,
  ShieldUserLinear,
  SquareAcademicCap2Linear,
  SquareAcademicCapLinear,
  StructureLinear,
  UserRoundedLinear,
  UsersGroupRoundedLinear,
} from '@assessprep/icons/solar';
import type { ComponentType, SVGProps } from 'react';

export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const GROUP_ICONS: Record<string, IconComponent> = {
  international: GlobalLinear,
  'us-network': StructureLinear,
  'us-state': FlagLinear,
  'ca-province': MapPointLinear,
  'uk-trust': BuildingsLinear,
  'india-group': SquareAcademicCapLinear,
  faith: BookBookmarkLinear,
  'national-curriculum': Book2Linear,
};

const ORG_KIND_ICONS: Record<DemoOrgKind, IconComponent> = {
  district: Buildings2Linear,
  board: Buildings2Linear,
  network: StructureLinear,
  school: SquareAcademicCapLinear,
  international: GlobalLinear,
};

// The payload carries no school level, so a K–12 tile reads its level from the
// title. First match wins.
const SCHOOL_LEVEL_ICONS: [RegExp, IconComponent][] = [
  [/elementary/i, BackpackLinear],
  [/middle/i, BookLinear],
  [/high|secondary|academy/i, SquareAcademicCap2Linear],
];

const ROLE_ICONS: Record<string, IconComponent> = {
  admin: ShieldUserLinear,
  teacher: PresentationGraphLinear,
  student: UserRoundedLinear,
  parent: UsersGroupRoundedLinear,
  qb_author: PenLinear,
};

export const JURISDICTION_ICON: IconComponent = MapPointLinear;

export const groupIcon = (key: string): IconComponent => GROUP_ICONS[key] ?? BuildingsLinear;

export const orgIcon = (kind: DemoOrg['kind'], title: string): IconComponent => {
  const match = kind === 'school' && SCHOOL_LEVEL_ICONS.find(([level]) => level.test(title));
  if (match) return match[1];

  return (kind && ORG_KIND_ICONS[kind]) ?? BuildingsLinear;
};

export const roleIcon = (role: string): IconComponent => ROLE_ICONS[role] ?? UserRoundedLinear;
